"use client";

import { c as _c } from "react/compiler-runtime";
import { Box } from "@mantine/core";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import classes from "./sidebar-layout-provider.module.scss";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
const MENU_FADE_OUT_MS = 500;
const MENU_FADE_IN_MS = 500;
const ICON_FADE_IN_MS = 120;

/** Desktop open/close animation phase. Idle on mobile and between transitions. */
export type SidebarPhase = "idle" | "menu-in" | "menu-out" | "icons-in";
export type SidebarLayoutContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  phase: SidebarPhase;
  /** Use collapsed (icon-only) layout for nav items */
  showCollapsedVisual: boolean;
};
const SidebarLayoutContext = createContext<SidebarLayoutContextValue | null>(null);
export function useSidebarLayout() {
  const context = useContext(SidebarLayoutContext);
  if (!context) {
    throw new Error("useSidebarLayout must be used within a SidebarLayoutProvider.");
  }
  return context;
}
export type SidebarLayoutProviderProps = {
  children?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
};
function useSidebarPhase(open, isMobile) {
  const $ = _c(3);
  const [phase, setPhase] = useState("idle");
  const [prevOpen, setPrevOpen] = useState(open);
  if (isMobile) {
    if (phase !== "idle") {
      setPhase("idle");
    }
    if (open !== prevOpen) {
      setPrevOpen(open);
    }
  } else {
    if (open !== prevOpen) {
      setPrevOpen(open);
      setPhase(open ? "menu-in" : "menu-out");
    }
  }
  let t0;
  let t1;
  if ($[0] !== phase) {
    t0 = () => {
      if (phase === "idle") {
        return;
      }
      let ms = ICON_FADE_IN_MS;
      let next = "idle";
      if (phase === "menu-in") {
        ms = MENU_FADE_IN_MS;
      } else {
        if (phase === "menu-out") {
          ms = MENU_FADE_OUT_MS;
          next = "icons-in";
        }
      }
      const timer = window.setTimeout(() => setPhase(next), ms);
      return () => window.clearTimeout(timer);
    };
    t1 = [phase];
    $[0] = phase;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return isMobile ? "idle" : phase;
}
function useSidebarHotkey(toggle) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== toggle) {
    t0 = () => {
      const onKeyDown = event => {
        if (event.key !== SIDEBAR_KEYBOARD_SHORTCUT || !(event.metaKey || event.ctrlKey)) {
          return;
        }
        event.preventDefault();
        toggle();
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t1 = [toggle];
    $[0] = toggle;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}
export function SidebarLayoutProvider(t0) {
  const $ = _c(17);
  const {
    defaultOpen: t1,
    className,
    children
  } = t0;
  const defaultOpen = t1 === undefined ? false : t1;
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(defaultOpen);
  const phase = useSidebarPhase(open, isMobile);
  let t2;
  if ($[0] !== isMobile) {
    t2 = () => {
      if (isMobile) {
        return;
      }
      setOpen(_temp);
    };
    $[0] = isMobile;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggleSidebar = t2;
  useSidebarHotkey(toggleSidebar);
  const showCollapsedVisual = !open && !isMobile && phase !== "menu-out";
  const dataState = open ? "expanded" : "collapsed";
  let t3;
  if ($[2] !== isMobile || $[3] !== open || $[4] !== phase || $[5] !== showCollapsedVisual || $[6] !== toggleSidebar) {
    t3 = {
      open,
      setOpen,
      isMobile,
      toggleSidebar,
      phase,
      showCollapsedVisual
    };
    $[2] = isMobile;
    $[3] = open;
    $[4] = phase;
    $[5] = showCollapsedVisual;
    $[6] = toggleSidebar;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const contextValue = t3;
  let t4;
  if ($[8] !== className) {
    t4 = cn(classes.wrapper, className);
    $[8] = className;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== dataState || $[12] !== t4) {
    t5 = <Box data-slot="sidebar-wrapper" data-state={dataState} className={t4}>{children}</Box>;
    $[10] = children;
    $[11] = dataState;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== contextValue || $[15] !== t5) {
    t6 = <SidebarLayoutContext.Provider value={contextValue}>{t5}</SidebarLayoutContext.Provider>;
    $[14] = contextValue;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp(prev) {
  return !prev;
}